import { type SubmissionHandler, useField, validate } from 'vee-validate'
import type { Ref } from 'vue'
import type { VueInstance } from '@vueuse/core'
import type { CheckboxGroupOption } from '@/models/formidable'

export function useForm<_, FieldsType extends object>({
  fetcher,
  initialValues,
  onSuccess,
  onError,
}: {
  fetcher: (..._args: any) => any
  initialValues: FieldsType
  onSuccess?: (..._args: any) => any
  onError?: (..._args: any) => any
}) {
  const successMessage = ref('')
  const form = reactive(initialValues)
  const isSuccessSubmitted = ref<boolean>(false)

  const handleSubmit: SubmissionHandler = async (
    values,
    { resetForm, setFieldError },
  ) => {
    try {
      const response = await fetcher(values)

      if (response?.message) {
        if (response?.message === 'Your password was changed successfully.') {
          isSuccessSubmitted.value = true
        }
        successMessage.value = response.message
      }

      Object.assign(form, initialValues)
      resetForm({ values: initialValues })

      if (onSuccess) onSuccess()
    } catch (error) {
      if (onError) onError({ error, setFieldError, form: initialValues })
    }
  }

  return {
    form,
    successMessage,
    handleSubmit,
    isSuccessSubmitted,
  }
}

/**
 * Returns a function to toggle all the values in a Vee Validate's
 * checkbox fieldset (`toggleAll`), and a reactive variable to track whether
 * the whole checkbox fieldset is selected or not (`isToggleOn`).
 *
 * @param {String} fieldsetName A Vee Validate's checkbox field
 *    name.
 *
 * @param {String} fieldsetRules Vee Validate's checkbox field
 *    name.
 *
 * @param {Array} fieldsetOptions The list of possible options
 *    within that fieldset.
 */
export function useToggleField({
  fieldsetName,
  fieldsetRules,
  fieldsetOptions,
}: {
  fieldsetName: string
  fieldsetRules: string
  fieldsetOptions: CheckboxGroupOption[]
}) {
  const isToggleOn = ref(false)

  const { value: selectedOptions, setValue } = useField(
    fieldsetName,
    fieldsetRules,
    {
      type: 'checkbox',
    },
  )

  watch(
    () => selectedOptions.value,
    (options: string[]) => {
      isToggleOn.value = options.length === fieldsetOptions.length
    },
  )

  const toggleAll = () => {
    isToggleOn.value = !isToggleOn.value

    if (isToggleOn.value) {
      const allOptions = fieldsetOptions.map((option) => option.label)
      setValue(allOptions)
    } else {
      setValue([])
    }
  }

  return {
    toggleAll,
    isToggleOn,
  }
}

/**
 * Triggers the blur event (and therefore the onBlur validation)
 * as soon as `modelValue` becomes valid.
 *
 * @param {String} field A Vue reference of the field.
 *
 * @param {String} modelValue The model you want to watch.
 *
 * @param {Array} rules The Vee Validate rules for this field.
 */
export function useValidFieldTracker(
  field: Ref<VueInstance>,
  modelValue: Ref<unknown>,
  rules: string,
) {
  watch(modelValue, async (value) => {
    const { valid } = await validate(value, rules)

    if (valid) {
      field.value.$el.dispatchEvent(new Event('blur'))
    }
  })
}
